import { AxiosError } from 'axios';
import clsx from 'clsx';
import React, { useState } from 'react';

import { APIErrorResponseGenerator } from '@/lib/helper';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import Auth from '@/components/app/auth/components';
import CommentCard from '@/components/app/product/components/@shared/comment-card';
import CommentsHeader from '@/components/app/product/components/@shared/comments-header';
import Section from '@/components/app/product/components/@shared/section';
import { VOTE } from '@/components/app/product/components/@shared/type';
import { AiComment } from '@/components/app/product/components/comments/ai-comment';
import Props from '@/components/app/product/components/content/comments-summary/type';
import { usePostVote } from '@/components/app/product/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import useCommonModalStore from '@/store/common-modal-store';

import { DATA_CY_COMMENTS_SUMMARY } from '@/constant/cypress';
import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function CommentsSummary({
  comments,
  rate,
  handleShowMoreComments,
  aiReviewSummary,
  ...props
}: Props) {
  const isSmallScreen = useClientSmallScreen();
  const { setShow } = useCommonModalStore();
  const { isAuthenticated } = useGetProfile();
  const { mutate: postVote } = usePostVote();
  const [isVotePending, setIsVotePending] = useState();

  const openLoginModal = () =>
    setShow(true, {
      DialogPanelProps: {
        className: 'rounded-xl overflow-hidden shadow-none!  p-0! bg-transparent! ',
      },
      Body() {
        return <Auth />;
      },
    });

  const handleVote = (id, vote, isAi = false) => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    setIsVotePending(id);
    postVote(
      { id, vote },
      {
        onSuccess: () => {
          setIsVotePending(undefined);
          if (isAi) {
            showToast({ message: productLocalization.registered, type: 'success' });
          }
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(error));
          }
          setIsVotePending(undefined);
        },
      },
    );
  };

  return (
    <Section
      data-cy={DATA_CY_COMMENTS_SUMMARY}
      id='commentsSection'
      expandable
      showMoreLabel={!isSmallScreen ? productLocalization.seeAllComments : undefined}
      onShowMoreClick={handleShowMoreComments}
      expandOnShowMoreClick={false}
      className={clsx('mx-0! px-0!', aiReviewSummary && 'max-h-[600px]! md:max-h-[700px]!')}
    >
      <CommentsHeader
        className='px-3.5 md:px-[30px]'
        rate={rate}
        onAddCommentClick={handleShowMoreComments}
        isCommentsSummary={true}
      />
      {aiReviewSummary && (
        <Container className='mx-3.5 md:mx-[30px]'>
          <AiComment
            aiReviewSummary={aiReviewSummary}
            handleVote={handleVote}
            isVotePending={isVotePending}
          />
        </Container>
      )}
      {comments.map((comment) => (
        <Container
          key={comment.id}
          className='mx-3.5 my-5 rounded-lg border-[1px] border-[#E9EBF1] pt-2.5! pb-6 md:mx-[30px] md:pt-0'
        >
          <CommentCard
            {...comment}
            handleVote={handleVote}
            isVotePending={isVotePending}
            className='px-6!'
          />
        </Container>
      ))}
    </Section>
  );
}
